export type RiskLevel = 'low' | 'medium' | 'high' | 'critical'

export interface RiskConfig {
  level: RiskLevel
  colorClasses: string
  label: string
  description: string
}

/** Absolute risk score thresholds, calibrated for a maxScore of 50. */
export const RISK_THRESHOLDS = {
  LOW: 15,
  MEDIUM: 30,
  HIGH: 40,
  CRITICAL: 50,
} as const

export const RISK_SCORING = {
  MAX_POSSIBLE_SCORE: 100,
  SEGMENTS_COUNT: 42
} as const

export function getRiskLevel(score: number): RiskLevel {
  if (score > RISK_THRESHOLDS.HIGH) return 'critical'
  if (score > RISK_THRESHOLDS.MEDIUM) return 'high'
  if (score > RISK_THRESHOLDS.LOW) return 'medium'
  return 'low'
}

export function getRiskConfig(score: number): RiskConfig {
  const level = getRiskLevel(score)
  
  const configs: Record<RiskLevel, Omit<RiskConfig, 'level'>> = {
    low: {
      colorClasses: 'text-status-success-text bg-status-success-bg border-status-success-border',
      label: 'Low Risk',
      description: 'Transaction appears safe'
    },
    medium: {
      colorClasses: 'text-status-warning-text bg-status-warning-bg border-status-warning-border',
      label: 'Medium Risk',
      description: 'Transaction requires attention'
    },
    high: {
      colorClasses: 'text-status-error-text bg-status-error-bg border-status-error-border',
      label: 'High Risk',
      description: 'Transaction requires immediate review'
    },
    critical: {
      colorClasses: 'text-status-error-text bg-status-error-bg border-status-error-border',
      label: 'Critical Risk',
      description: 'Transaction blocked - requires urgent attention'
    }
  }

  return { level, ...configs[level] }
}

export function getRiskBarColor(score: number): string {
  const level = getRiskLevel(score)
  const colors = {
    low: 'bg-success',
    medium: 'bg-warning',
    high: 'bg-destructive',
    critical: 'bg-destructive'
  }
  return colors[level]
}

export function getRiskTextColor(score: number): string {
  const level = getRiskLevel(score)
  const colors = {
    low: 'text-status-success-text',
    medium: 'text-status-warning-text',
    high: 'text-status-error-text',
    critical: 'text-status-error-text'
  }
  return colors[level]
}

export function formatRiskScore(score: number): string {
  return Math.round(score).toString()
}